import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function Brochure() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-white text-slate-900 print:bg-white print:text-black"
    >
      <div className="max-w-4xl mx-auto px-4 py-6 sm:py-8 print:px-0 print:py-0">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-8 print:mb-6">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4">
            <img src="/logo.png" alt="NCAI" className="h-10 sm:h-12 w-auto" />
            <span className="text-xl sm:text-2xl font-extrabold tracking-tight">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-violet-600 to-indigo-600">NCAI</span>
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight mb-2">Course Brochure</h1>
          <p className="text-slate-600 text-sm sm:text-base">NeuraCore AI Institute - Empowering Future Careers</p>
        </div>

        {/* Digital Marketing Course */}
        <section className="mb-8 sm:mb-12 print:mb-8 page-break-inside-avoid">
           <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl p-4 sm:p-6 text-white mb-4 sm:mb-6">
             <h2 className="text-xl sm:text-2xl font-bold mb-2">Certificate Course in Digital Marketing</h2>
             <div className="inline-flex items-center rounded-full bg-emerald-100 px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold text-emerald-700">
               12 Weeks Duration
             </div>
           </div>

           <div className="space-y-4 sm:space-y-6">
             <div>
               <h3 className="text-lg sm:text-xl font-bold mb-3 text-slate-900">About the Course</h3>
               <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                 The Certificate Course in Digital Marketing imparts practical skills in digital marketing strategies and tools to help individuals navigate the online landscape effectively.
               </p>
             </div>

             <div>
               <h3 className="text-lg sm:text-xl font-bold mb-3 text-slate-900">What the Course Covers</h3>
               <ul className="space-y-2">
                 {[
                   "Introduction to Digital Marketing",
                   "Search Engine Optimization (SEO)",
                   "Search Engine Marketing (SEM) and Pay-Per-Click (PPC)",
                   "Social Media Marketing",
                   "Content Marketing",
                   "Email Marketing",
                   "Digital Analytics and Tools",
                   "Marketing Automation"
                 ].map((item, index) => (
                   <li key={index} className="flex gap-3">
                     <span className="mt-1 h-2 w-2 rounded-full bg-emerald-500 flex-shrink-0" />
                     <span className="text-slate-700 text-sm sm:text-base">{item}</span>
                   </li>
                 ))}
               </ul>
             </div>

             <div>
               <h3 className="text-lg sm:text-xl font-bold mb-3 text-slate-900">Course Structure</h3>
               <p className="text-slate-600 mb-4 text-sm sm:text-base">The course is divided into three parts:</p>
               <div className="grid gap-3 sm:gap-4 md:grid-cols-3">
                 <div className="bg-slate-50 rounded-xl p-3 sm:p-4 text-center">
                   <div className="text-base sm:text-lg font-semibold text-slate-900">Theory</div>
                 </div>
                 <div className="bg-slate-50 rounded-xl p-3 sm:p-4 text-center">
                   <div className="text-base sm:text-lg font-semibold text-slate-900">Practical</div>
                 </div>
                 <div className="bg-slate-50 rounded-xl p-3 sm:p-4 text-center">
                   <div className="text-base sm:text-lg font-semibold text-slate-900">Industry Internship</div>
                 </div>
               </div>
             </div>

             <div>
               <h3 className="text-lg sm:text-xl font-bold mb-3 text-slate-900">Job Opportunities</h3>
               <p className="text-slate-600 mb-4 text-sm sm:text-base">Candidates can get jobs in digital marketing agencies, e-commerce companies, and tech firms as:</p>
               <div className="grid gap-3 md:grid-cols-2">
                 {[
                   "Digital Marketing Specialist",
                   "SEO Analyst",
                   "Social Media Manager",
                   "Content Marketing Manager",
                   "PPC Specialist",
                   "Marketing Analyst",
                   "Email Marketing Coordinator",
                   "Digital Campaign Manager"
                 ].map((job, index) => (
                   <div key={index} className="flex items-center gap-3 bg-emerald-50 rounded-lg p-3">
                     <span className="text-emerald-600">📈</span>
                     <span className="text-slate-700 font-medium text-sm sm:text-base">{job}</span>
                   </div>
                 ))}
               </div>
             </div>
           </div>
         </section>

        {/* AI & Machine Learning Course */}
        <section className="mb-12 print:mb-8 page-break-inside-avoid">
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-6 text-white mb-6">
            <h2 className="text-2xl font-bold mb-2">Certificate Course in AI & Machine Learning</h2>
            <div className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1 text-sm font-semibold text-violet-700">
              16 Weeks Duration
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">About the Course</h3>
              <p className="text-slate-600 leading-relaxed">
                The Certificate Course in AI & Machine Learning imparts practical skills in the fundamentals and applications of artificial intelligence and machine learning technologies.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">What the Course Covers</h3>
              <ul className="space-y-2">
                {[
                  "Introduction to Artificial Intelligence and Machine Learning",
                  "Programming fundamentals with Python",
                  "Data collection, cleaning, and preprocessing",
                  "Statistical analysis and data visualization",
                  "Supervised and unsupervised learning algorithms",
                  "Deep learning and neural networks",
                  "Model evaluation and deployment",
                  "Ethical considerations in AI",
                  "Tools and frameworks like TensorFlow, PyTorch, scikit-learn"
                ].map((item, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-violet-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Course Structure</h3>
              <p className="text-slate-600 mb-4">The course is divided into three parts:</p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-lg font-semibold text-slate-900">Theory</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-lg font-semibold text-slate-900">Practical</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-lg font-semibold text-slate-900">Industry Internship</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Job Opportunities</h3>
              <p className="text-slate-600 mb-4">Candidates can get jobs in tech companies, research labs, and data-driven industries as:</p>
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  "Machine Learning Engineer",
                  "Data Scientist",
                  "AI Research Assistant",
                  "Data Analyst",
                  "AI Product Manager",
                  "Computer Vision Engineer",
                  "NLP Specialist",
                  "Robotics Engineer"
                ].map((job, index) => (
                  <div key={index} className="flex items-center gap-3 bg-violet-50 rounded-lg p-3">
                    <span className="text-violet-600">🤖</span>
                    <span className="text-slate-700 font-medium">{job}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hardware Design & Manufacturing Course */}
        <section className="mb-12 print:mb-8 page-break-inside-avoid">
          <div className="bg-gradient-to-r from-orange-500 to-rose-500 rounded-2xl p-6 text-white mb-6">
            <h2 className="text-2xl font-bold mb-2">Certificate Course in Printed Circuit Board Manufacturing</h2>
            <div className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-sm font-semibold text-orange-700">
              12 Weeks Duration
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">About the Course</h3>
              <p className="text-slate-600 leading-relaxed">
                The Certificate Course in Printed Circuit Board Manufacturing imparts practical skills used in different processes of PCB manufacturing.
              </p>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">What the Course Covers</h3>
              <ul className="space-y-2">
                {[
                  "Introduction to different types of PCBs",
                  "Familiarization of electronic components",
                  "Process of PCB manufacturing for Single-sided, Double-sided, and Multilayer PCBs",
                  "Inspection and testing of PCBs",
                  "Raw materials used in PCBs",
                  "Application of IPC standards",
                  "Safety and environmental concerns",
                  "Machine maintenance"
                ].map((item, index) => (
                  <li key={index} className="flex gap-3">
                    <span className="mt-1 h-2 w-2 rounded-full bg-orange-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Course Structure</h3>
              <p className="text-slate-600 mb-4">The course is divided into three parts:</p>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-lg font-semibold text-slate-900">Theory</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-lg font-semibold text-slate-900">Practical</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <div className="text-lg font-semibold text-slate-900">Industry Internship</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-xl font-bold mb-3 text-slate-900">Job Opportunities</h3>
              <p className="text-slate-600 mb-4">Candidates can get jobs in PCB manufacturing facilities as:</p>
              <div className="grid gap-3 md:grid-cols-2">
                {[
                  "CNC Drilling & Routing Operator",
                  "Circuit Imaging Operator",
                  "Plating & Etching Operator",
                  "Solder Mask Printing Operator",
                  "HAL Machine Operator",
                  "Flying Probe Machine Operator",
                  "Production Planning and Control Technician",
                  "Quality Control Technician"
                ].map((job, index) => (
                  <div key={index} className="flex items-center gap-3 bg-orange-50 rounded-lg p-3">
                    <span className="text-orange-600">💼</span>
                    <span className="text-slate-700 font-medium">{job}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="text-center mt-8 sm:mt-12 print:mt-8 border-t border-slate-200 pt-6 sm:pt-8 print:pt-4">
          <p className="text-slate-600 mb-4 text-sm sm:text-base">Ready to start your career journey?</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
            <Link
              to="/apply"
              className="inline-flex items-center justify-center rounded-xl px-4 sm:px-6 py-3 font-semibold bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-lg transition text-sm sm:text-base"
            >
              Apply Now
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center rounded-xl px-4 sm:px-6 py-3 font-semibold border border-slate-300 hover:border-slate-400 text-sm sm:text-base"
            >
              Back to Home
            </Link>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-4">© {new Date().getFullYear()} NeuraCore AI Institute (NCAI). All rights reserved.</p>
        </div>
      </div>
    </motion.div>
  );
}